import { useState } from 'react';
import {
    Button,
    Typography,
    Stack,
    IconButton,
    DialogContent,
    DialogActions,
    Alert,
    MenuItem,
    TextField,
} from '@mui/material';
import DialogHeader from '@shared/ui/dialog/DialogHeader';
import { Close } from '@mui/icons-material';
import { APP_CONFIG } from '../../../../config/app.config';
import { container } from '@infrastructure/container';
import { AmazonRegion } from '@domain/connection/IMarketplaceRepository';
import { Connection } from '@domain/connection/models/Connection';
import { disableReferrer, saveAmazonOAuthIntent } from '../amazonOAuth';

interface AmazonCredentialsFormProps {
    onClose: () => void;
    onNewConnection?: (connection: Connection) => void;
    /** When set, Seller Central sign-in re-authorizes this connection. */
    reconnectConnectionId?: string;
}

/** Vite mode, not VITE_APP_ENV. Production and demo builds omit the sandbox connect action. */
const showSandboxConnect = import.meta.env.MODE === 'development';

const REGIONS: { value: AmazonRegion; label: string }[] = [
    { value: 'eu', label: 'Europe' },
    { value: 'na', label: 'North America' },
    { value: 'fe', label: 'Far East' },
];

const AmazonCredentialsForm = ({ onClose, onNewConnection, reconnectConnectionId }: AmazonCredentialsFormProps) => {
    const [region, setRegion] = useState<AmazonRegion>('eu');
    const [error, setError] = useState<string | null>(null);
    const [isLoading, setIsLoading] = useState(false);

    const connectSandbox = async () => {
        setError(null);
        setIsLoading(true);
        try {
            const result = await container.amazonConnectorRepository.connectSandbox(region, reconnectConnectionId);
            if (result.status === 'error') {
                setError(result.error?.message || 'Failed to connect the Amazon sandbox');
                setIsLoading(false);
                return;
            }
            if (!result.data.active) {
                window.location.assign(`/company/connections/${result.data.id}/setup`);
                return;
            }
            if (onNewConnection) onNewConnection(result.data);
            else onClose();
        } catch (err) {
            setError(err instanceof Error ? err.message : 'Failed to connect the Amazon sandbox');
            setIsLoading(false);
        }
    };

    const signIn = async () => {
        setError(null);
        setIsLoading(true);
        try {
            const result = await container.amazonConnectorRepository.startAuthorization(region, reconnectConnectionId);
            if (result.status === 'error') {
                setError(result.error?.message || 'Failed to start Amazon sign-in');
                setIsLoading(false);
                return;
            }

            saveAmazonOAuthIntent({
                region,
                connectionId: reconnectConnectionId,
                returnPath: `${window.location.pathname}${window.location.search}`,
            });
            disableReferrer();
            window.location.href = result.data.authUrl;
        } catch (err) {
            setError(err instanceof Error ? err.message : 'Failed to start Amazon sign-in');
            setIsLoading(false);
        }
    };

    return (
        <>
            <DialogHeader>
                <Stack direction="row" justifyContent="space-between" alignItems="center">
                    <Typography variant="h6" fontWeight="medium">
                        {reconnectConnectionId ? 'Reconnect Amazon' : 'Connect Amazon'}
                    </Typography>
                    <IconButton onClick={onClose} color="inherit">
                        <Close />
                    </IconButton>
                </Stack>
            </DialogHeader>

            <DialogContent>
                <Typography variant="body2" color="text.secondary">
                    Sign in with the Seller Central account that should share inventory and orders.
                    Amazon asks you to approve Juststock, then sends you back here.
                </Typography>
                <TextField
                    select
                    fullWidth
                    label="Seller region"
                    value={region}
                    onChange={(event) => setRegion(event.target.value as AmazonRegion)}
                    margin="normal"
                >
                    {REGIONS.map(option => (
                        <MenuItem key={option.value} value={option.value}>{option.label}</MenuItem>
                    ))}
                </TextField>
                {showSandboxConnect && (
                    <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>
                        Sandbox uses the refresh token from Solution Provider Portal.
                        It does not open Seller Central.
                    </Typography>
                )}
                {error && (
                    <Alert severity="error" sx={{ mt: 2 }}>{error}</Alert>
                )}
            </DialogContent>

            <DialogActions sx={{ borderTop: '1px solid', borderColor: APP_CONFIG.ui.theme.colors.border, p: 3 }}>
                <Stack direction="row" alignItems="center" gap={1} flex={1} justifyContent="space-between">
                    <Button variant="outlined" onClick={onClose}>Back</Button>
                    <Stack direction="row" gap={1}>
                        {showSandboxConnect && (
                            <Button variant="outlined" disabled={isLoading} onClick={connectSandbox}>
                                {isLoading ? 'Connecting…' : 'Connect sandbox'}
                            </Button>
                        )}
                        <Button variant="contained" disabled={isLoading} onClick={signIn}>
                            {isLoading ? 'Redirecting…' : 'Sign in with Amazon'}
                        </Button>
                    </Stack>
                </Stack>
            </DialogActions>
        </>
    );
};

export default AmazonCredentialsForm;
